import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, ChevronDown, ChevronUp, X } from "lucide-react";

/**
 * CookieConsent — lightweight UK-GDPR / PECR compliant first-visit banner.
 *
 * Choice model:
 *   - "Accept all"   → essential + payment/fraud (Stripe risk) cookies.
 *   - "Essential only" → strictly-necessary cookies only (auth session).
 *
 * The decision is persisted to `localStorage["mttApp:cookieConsent"]` as
 * `{level: "essential" | "all", at: <ISO string>, v: 1}`. On subsequent
 * visits the banner does not re-render.
 *
 * Rendered globally from `App.js`. Anchored bottom-left with a `sm:` breakpoint
 * so it doesn't feel like an interstitial on mobile.
 */
const STORAGE_KEY = "mttApp:cookieConsent";
const VERSION = 1;

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== VERSION) return null;
    return parsed;
  } catch (_) { return null; }
}

function writeStored(level) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ level, at: new Date().toISOString(), v: VERSION }));
  } catch (_) { /* localStorage unavailable — ignore */ }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Small delay so the banner slides in after the page has painted rather
    // than clashing with the initial hero animation.
    const t = setTimeout(() => {
      if (!readStored()) setOpen(true);
    }, 700);
    // Listen for a "reopen" broadcast from the footer link so parents can
    // change their mind at any time — required for full ICO / PECR
    // compliance ("consent must be as easy to withdraw as to give").
    const onReopen = () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
      setExpanded(true);
      setOpen(true);
    };
    window.addEventListener("mtt:cookie-consent-reopen", onReopen);
    return () => {
      clearTimeout(t);
      window.removeEventListener("mtt:cookie-consent-reopen", onReopen);
    };
  }, []);

  const accept = (level) => {
    writeStored(level);
    setOpen(false);
    // Broadcast a synthetic event so future integrations (analytics loaders,
    // Stripe risk hook) can lazy-init only after consent.
    try { window.dispatchEvent(new CustomEvent("mtt:cookie-consent", { detail: { level } })); } catch (_) { /* noop */ }
  };

  if (!open) return null;

  return (
    <div
      className="fixed z-50 inset-x-3 bottom-3 sm:inset-x-auto sm:left-4 sm:bottom-4 sm:max-w-md"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="rounded-3xl border border-slate-200 bg-white shadow-pop p-5 pr-4 relative">
        <button
          type="button"
          onClick={() => accept("essential")}
          className="absolute top-3 right-3 text-slate-400 hover:text-brandSlate transition"
          aria-label="Close cookie banner and keep essential cookies only"
          data-testid="cookie-consent-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-brandPink/10 text-brandPink flex-shrink-0">
            <Cookie className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-semibold text-brandSlate">Cookies &amp; your privacy</div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              We use <strong>essential</strong> cookies to keep you signed in and secure. With your permission we also enable{" "}
              <strong>payment &amp; fraud-prevention</strong> cookies (e.g. Stripe risk analysis) so wallet top-ups and payouts stay safe.
            </p>

            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-brandTeal hover:text-brandTealDark"
              data-testid="cookie-consent-toggle-details"
              aria-expanded={expanded}
            >
              {expanded ? (<><ChevronUp className="w-3 h-3" /> Hide details</>) : (<><ChevronDown className="w-3 h-3" /> What&rsquo;s inside?</>)}
            </button>

            {expanded && (
              <div className="mt-2 space-y-2 text-xs text-slate-600 leading-relaxed" data-testid="cookie-consent-details">
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2">
                  <div className="font-bold text-emerald-800 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Essential (always on)</div>
                  <div className="text-emerald-900/85 mt-0.5">Session cookie for signing in, PIN gates, and safety-check state. Cannot be disabled — the site would not work without them.</div>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="font-bold text-brandSlate">Payment &amp; fraud prevention (opt-in)</div>
                  <div className="text-slate-600 mt-0.5">Stripe drops small cookies during checkout and payouts to detect fraud and confirm your identity (KYC/AML). Opt out and you can still browse, but you won&rsquo;t be able to complete Stripe top-ups or withdrawals.</div>
                </div>
                <div className="text-[11px] text-slate-500">
                  We do not use advertising or profiling cookies. Full details in our{" "}
                  <Link
                    to="/info/privacy"
                    className="text-brandTeal underline hover:no-underline"
                    onClick={() => accept("essential")}
                    data-testid="cookie-consent-privacy-link"
                  >
                    Privacy Policy
                  </Link>.
                </div>
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => accept("essential")}
                className="inline-flex items-center justify-center px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-bold text-xs hover:bg-slate-50 transition"
                data-testid="cookie-consent-essential"
              >
                Essential only
              </button>
              <button
                type="button"
                onClick={() => accept("all")}
                className="inline-flex items-center justify-center px-3 py-2 rounded-xl bg-brandTeal text-white font-bold text-xs hover:bg-brandTealDark transition"
                data-testid="cookie-consent-accept-all"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
